/**
 * O que faz: renderiza uma única bolha de conversa sem repetir trabalho quando outras mensagens chegam.
 * Onde usa: ChatWindow, dentro dos grupos paginados por data.
 * Conexões: ChatMessage, renderer de texto/mídia, componentes de follow-up e ChatWindow.css (cores das bolhas).
 * Tabelas: nenhuma; recebe dados já carregados pelo hook de mensagens.
 * Data: 24/08/2026 (design system Gens em 07/10/2026: bolhas musgo/lima, selo IA com texto e ícone, leitura com ícone).
 */
import { memo, type ReactNode } from 'react';
import { Bot, Check, CheckCheck, Repeat2 } from 'lucide-react';
import type { ChatMessage } from '@/features/chat/messages/chatMessageTypes';
import { ChatAdPreviewCard } from './ChatAdPreviewCard';
import type { LeadAdPreview } from '@/hooks/useLeadAdPreviews';

interface ChatMessageBubbleProps {
  message: ChatMessage;
  isUser: boolean;
  isNextFromSameSender: boolean;
  formatMessageTime: (dateString: string) => string;
  renderMessageContent: (message: ChatMessage) => ReactNode;
  adPreview?: LeadAdPreview;
}

export const ChatMessageBubble = memo(({
  message,
  isUser,
  isNextFromSameSender,
  formatMessageTime,
  renderMessageContent,
  adPreview,
}: ChatMessageBubbleProps) => {
  // IA identificada por texto + ícone, não só por cor (regra de acessibilidade do design system).
  const enviadaPelaIA = isUser && !message.follow_up_execucao_id && String(message.enviado_por).toLowerCase() === 'ia';

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} ${!isNextFromSameSender ? 'mb-2' : ''}`}>
      {/* Sem sombra nem hover animado: em conversa longa isso custa pintura e o design system veta. */}
      <div
        className={`
          chat-bubble-enter relative max-w-[85%] break-words px-3.5 py-2 text-foreground md:max-w-[75%] 2xl:max-w-[65%]
          ${isUser ? 'chat-bolha--enviada' : 'chat-bolha--recebida'}
        `}
      >
        {isUser && message.follow_up_execucao_id && (
          <div className="mb-1.5 flex items-center gap-1.5 rounded-lg bg-card/70 px-2 py-1 text-[11px] font-medium text-accent-foreground">
            <Repeat2 className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            <span className="truncate">
              Enviada automaticamente
              {message.follow_up_execucoes?.follow_up_campaigns?.nome
                ? ` · ${message.follow_up_execucoes.follow_up_campaigns.nome}`
                : ''}
              {message.follow_up_execucoes?.follow_up_templates?.sequencia
                ? ` (etapa ${message.follow_up_execucoes.follow_up_templates.sequencia})`
                : ''}
            </span>
          </div>
        )}

        {enviadaPelaIA && (
          <div className="chat-selo-ia mb-1.5 inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-semibold">
            <Bot className="h-3 w-3" aria-hidden="true" />
            Assistente IA
          </div>
        )}

        {adPreview && <ChatAdPreviewCard anuncio={adPreview} />}

        <div className="text-[14.5px] leading-relaxed break-words selection:bg-primary/60 selection:text-foreground">
          {renderMessageContent(message)}
        </div>

        <div className="mt-1 flex items-center justify-end gap-1 text-[11px] leading-none text-muted-foreground tabular-nums">
          <span>{formatMessageTime(message.created_at)}</span>
          {isUser && (
            message.lida ? (
              <span className="text-accent-foreground" title="Lida">
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Lida</span>
              </span>
            ) : (
              <span title="Enviada">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Enviada</span>
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
});

ChatMessageBubble.displayName = 'ChatMessageBubble';
